import { Link } from 'react-router-dom';
import { ArrowRight, Megaphone } from 'lucide-react';
import { Card } from '@/components/Shared/Card';
import { Badge } from '@/components/Shared/Badge';
import type { ChecklistProtocolReport } from '@/store/useChecklistProtocolStore';
import { formatUtcToLocalTime } from '@/utils/dateUtils';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

interface ProtocolReportsCardProps {
  reports: ChecklistProtocolReport[];
  /** Opens the reports in "Offene Punkte", where Verwaltung handles them. */
  onOpenReports: () => void;
  t: Translate;
}

/** The reports ("Meldungen") employees submitted on the protocol's day, oldest first. */
export function ProtocolReportsCard({ reports, onOpenReports, t }: ProtocolReportsCardProps) {
  return (
    <Card
      title={t('reports.title')}
      actions={
        <Link
          to="/open-items"
          onClick={onOpenReports}
          className="inline-flex items-center gap-1 self-center text-[12px] font-medium text-[#00A871] hover:underline"
        >
          {t('reports.openAll')}
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
        </Link>
      }
    >
      <ul className="divide-y divide-slate-100">
        {reports.map((report) => (
          <li key={report.openItemId} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
            <Megaphone className="w-4 h-4 mt-0.5 shrink-0 text-violet-600" strokeWidth={2} aria-hidden />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-medium text-slate-900">{report.title}</span>
                <ReportStatus report={report} t={t} />
              </div>
              {report.note && <p className="mt-0.5 text-[12px] text-slate-500">{report.note}</p>}
              <div className="mt-0.5 text-[11px] text-slate-500">
                <span className="tabular-nums">{formatUtcToLocalTime(report.createdAt)}</span>
                {' · '}
                {[report.reportedByName, report.objectName].filter(Boolean).join(' · ')}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ReportStatus({ report, t }: { report: ChecklistProtocolReport; t: Translate }) {
  if (report.convertedTaskId) return <Badge variant="success">{t('reports.status.converted')}</Badge>;
  if (report.completedAt) return <Badge variant="success">{t('reports.status.done')}</Badge>;
  return <Badge variant="warning">{t('reports.status.open')}</Badge>;
}
